/**
 * LYT Communications - Flowerpot Installation Tracker
 * Version: 1.0.0
 * Created: 2026-02-22
 *
 * Simple single-phase workflow: Not Started → In Progress → Complete → QC Approved
 * Used for flowerpot/utility box (UG12) installation tracking.
 */

import React, { useState, useCallback } from 'react';
import { Camera, CheckCircle, ChevronDown, ChevronUp, Send, X, AlertTriangle } from 'lucide-react';

const INSTALL_STATUSES = ['Not Started', 'In Progress', 'Complete', 'QC Approved', 'Issue'];

const STATUS_COLORS = {
  'Not Started': '#FF4444',
  'In Progress': '#FFB800',
  'Complete': '#4CAF50',
  'QC Approved': '#2196F3',
  'Issue': '#FF9800',
};

function FlowerpotInstallTracker({ flowerpot, darkMode, user, onStatusUpdate, onPhotoUpload }) {
  const [expanded, setExpanded] = useState(false);
  const [notes, setNotes] = useState('');
  const [photos, setPhotos] = useState([]);
  const [showConfirm, setShowConfirm] = useState(null);

  const cardBg = darkMode ? '#1a2f4e' : '#ffffff';
  const borderColor = darkMode ? '#1e3a5f' : '#e2e8f0';
  const text = darkMode ? '#ffffff' : '#1e293b';
  const textMuted = darkMode ? '#8892b0' : '#64748b';

  const status = flowerpot?.status || 'Not Started';
  const isAdmin = user?.role === 'admin' || user?.role === 'Admin' || user?.role === 'Supervisor' || user?.role === 'QC Inspector';
  const isContractor = user?.role === 'contractor' || user?.role === 'Contractor' || user?.role === 'employee' || user?.role === 'Employee' || user?.role === 'sub' || user?.role === 'Sub';

  const handlePhotoCapture = useCallback((e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    const newPhotos = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name,
      type: 'install',
      timestamp: new Date().toISOString(),
    }));
    setPhotos(prev => [...prev, ...newPhotos]);
  }, []);

  const removePhoto = (index) => {
    setPhotos(prev => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  const confirmStatusChange = () => {
    if (onStatusUpdate && showConfirm) {
      onStatusUpdate({
        device_id: flowerpot.device_id,
        field: 'status',
        value: showConfirm,
        notes,
        photos: photos.map(p => p.file),
        timestamp: new Date().toISOString(),
        user: user?.email,
      });
    }
    setShowConfirm(null);
    setNotes('');
    setPhotos([]);
  };

  const getNextActions = () => {
    switch (status) {
      case 'Not Started':
        return isContractor ? [{ label: 'Start Install', status: 'In Progress', color: '#FFB800' }] : [];
      case 'In Progress':
        return isContractor ? [
          { label: 'Mark Complete', status: 'Complete', color: '#4CAF50', needsPhotos: true },
          { label: 'Report Issue', status: 'Issue', color: '#FF9800' },
        ] : [];
      case 'Complete':
        return isAdmin ? [
          { label: 'QC Approve', status: 'QC Approved', color: '#2196F3' },
          { label: 'Reject - Redo', status: 'In Progress', color: '#FF9800' },
        ] : [];
      case 'Issue':
        return isContractor ? [{ label: 'Issue Resolved', status: 'In Progress', color: '#FFB800' }] :
               isAdmin ? [{ label: 'Clear Issue', status: 'In Progress', color: '#FFB800' }] : [];
      default:
        return [];
    }
  };

  const actions = getNextActions();
  const statusColor = STATUS_COLORS[status] || '#9E9E9E';
  const progressPercent = INSTALL_STATUSES.indexOf(status) / (INSTALL_STATUSES.length - 2) * 100;

  return (
    <div style={{
      backgroundColor: cardBg,
      border: `1px solid ${borderColor}`,
      borderRadius: '12px',
      overflow: 'hidden',
      marginBottom: '12px',
    }}>
      {/* Header */}
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 16px', cursor: 'pointer',
          borderLeft: `4px solid ${statusColor}`,
        }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <span style={{ fontWeight: '700', fontSize: '0.95rem', color: text }}>
              Installation
            </span>
            <span style={{
              padding: '2px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600',
              backgroundColor: statusColor + '20', color: statusColor,
            }}>
              {status}
            </span>
          </div>
          <div style={{ fontSize: '0.8rem', color: textMuted }}>
            UG12 Utility Box • {flowerpot?.contractor_id || 'Unassigned'}
          </div>
        </div>
        {expanded ? <ChevronUp size={20} color={textMuted} /> : <ChevronDown size={20} color={textMuted} />}
      </div>

      {/* Progress bar */}
      <div style={{ height: '3px', backgroundColor: darkMode ? '#0d1b2a' : '#e2e8f0' }}>
        <div style={{
          height: '100%', width: `${Math.min(progressPercent, 100)}%`,
          backgroundColor: statusColor, transition: 'width 0.3s ease',
        }} />
      </div>

      {/* Expanded content */}
      {expanded && (
        <div style={{ padding: '16px', borderTop: `1px solid ${borderColor}` }}>
          {/* Status timeline */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '0.8rem', fontWeight: '600', color: textMuted, marginBottom: '8px' }}>
              INSTALL PROGRESS
            </div>
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {INSTALL_STATUSES.filter(s => s !== 'Issue').map((s, i) => {
                const isActive = INSTALL_STATUSES.indexOf(status) >= i;
                const isCurrent = status === s;
                return (
                  <div key={s} style={{
                    flex: 1, minWidth: '60px', textAlign: 'center', padding: '6px 4px', borderRadius: '6px',
                    fontSize: '0.65rem', fontWeight: isCurrent ? '700' : '500',
                    backgroundColor: isActive ? (STATUS_COLORS[s] || '#9E9E9E') + '20' : (darkMode ? '#0d1b2a' : '#f1f5f9'),
                    color: isActive ? (STATUS_COLORS[s] || '#9E9E9E') : textMuted,
                    border: isCurrent ? `2px solid ${STATUS_COLORS[s]}` : '2px solid transparent',
                  }}>
                    {s}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Timestamps */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px', fontSize: '0.85rem' }}>
            {flowerpot?.notes && (
              <div style={{ gridColumn: '1 / -1' }}>
                <span style={{ color: textMuted }}>Notes: </span>
                <span style={{ color: text }}>{flowerpot.notes}</span>
              </div>
            )}
          </div>

          {/* Photo upload */}
          {(status === 'In Progress' && isContractor) && (
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: textMuted, marginBottom: '4px' }}>
                Installation Photos (2 min: before + after)
              </label>
              <label style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                padding: '12px', border: `2px dashed ${borderColor}`, borderRadius: '8px',
                cursor: 'pointer', color: textMuted, fontSize: '0.85rem',
              }}>
                <Camera size={18} />
                <span>Take or Upload Photos</span>
                <input type="file" accept="image/*" multiple onChange={handlePhotoCapture} style={{ display: 'none' }} />
              </label>
              {photos.length > 0 && (
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                  {photos.map((p, i) => (
                    <div key={i} style={{ position: 'relative', width: '60px', height: '60px', borderRadius: '8px', overflow: 'hidden' }}>
                      <img src={p.preview} alt={`Photo ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <button onClick={() => removePhoto(i)} style={{
                        position: 'absolute', top: 2, right: 2, width: 18, height: 18, borderRadius: '50%',
                        background: 'rgba(0,0,0,0.6)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <X size={10} color="#fff" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Notes */}
          {actions.length > 0 && (
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: textMuted, marginBottom: '4px' }}>
                Notes
              </label>
              <textarea
                value={notes} onChange={e => setNotes(e.target.value)}
                placeholder="Add installation notes..."
                rows={2}
                style={{
                  width: '100%', padding: '10px', borderRadius: '8px', fontSize: '0.85rem',
                  border: `1px solid ${borderColor}`, backgroundColor: darkMode ? '#0d1b2a' : '#f8fafc',
                  color: text, resize: 'none',
                }}
              />
            </div>
          )}

          {/* Action buttons */}
          {actions.length > 0 && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {actions.map(action => {
                const disabled = action.needsPhotos && photos.length < 2;
                return (
                  <button
                    key={action.label}
                    onClick={() => !disabled && setShowConfirm(action.status)}
                    disabled={disabled}
                    style={{
                      flex: 1, padding: '10px 16px', borderRadius: '8px', border: 'none',
                      backgroundColor: disabled ? '#555' : action.color,
                      color: '#fff', fontWeight: '600', fontSize: '0.85rem',
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      opacity: disabled ? 0.5 : 1,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                    }}
                  >
                    {action.label === 'QC Approve' ? <CheckCircle size={16} /> :
                     action.label === 'Report Issue' ? <AlertTriangle size={16} /> :
                     <Send size={16} />}
                    {action.label}
                    {action.needsPhotos && photos.length < 2 && ` (${2 - photos.length} photos needed)`}
                  </button>
                );
              })}
            </div>
          )}

          {/* QC Approved badge */}
          {status === 'QC Approved' && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 16px',
              borderRadius: '8px', backgroundColor: '#2196F3' + '15', color: '#2196F3',
              fontWeight: '600', fontSize: '0.9rem',
            }}>
              <CheckCircle size={20} /> Installation QC Approved
            </div>
          )}
        </div>
      )}

      {/* Confirmation dialog */}
      {showConfirm && (
        <div style={{
          padding: '16px', backgroundColor: darkMode ? '#0a1628' : '#fffbeb',
          borderTop: `1px solid ${borderColor}`,
        }}>
          <div style={{ fontSize: '0.9rem', fontWeight: '600', color: text, marginBottom: '8px' }}>
            Confirm: Change status to "{showConfirm}"?
          </div>
          {notes && <div style={{ fontSize: '0.8rem', color: textMuted, marginBottom: '8px' }}>Notes: {notes}</div>}
          {photos.length > 0 && <div style={{ fontSize: '0.8rem', color: textMuted, marginBottom: '8px' }}>{photos.length} photo(s) attached</div>}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={confirmStatusChange} style={{
              flex: 1, padding: '10px', borderRadius: '8px', border: 'none',
              backgroundColor: '#4CAF50', color: '#fff', fontWeight: '600', cursor: 'pointer',
            }}>
              Confirm
            </button>
            <button onClick={() => setShowConfirm(null)} style={{
              flex: 1, padding: '10px', borderRadius: '8px', border: `1px solid ${borderColor}`,
              backgroundColor: 'transparent', color: text, fontWeight: '600', cursor: 'pointer',
            }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default FlowerpotInstallTracker;
